/**
 * The quick-reply motion: the tapped chip becomes the creator's message. A copy of the chip (already dressed as
 * a sent bubble) flies from where the chip was to where the real bubble lands, which stays hidden until the
 * copy arrives (FLIP: both ends measured, only transform and opacity animate). If no new bubble shows up in
 * time, the copy just drops away. Nothing happens under reduced motion.
 */
import { EASE, dur, prefersReducedMotion } from '@/motion/tokens'

const USER_BUBBLE = '[data-testid="msg-user"]'
/** Give up looking for the new bubble after this many frames (~0.7s). */
const MAX_FRAMES = 42

/** y(x) of a CSS cubic-bezier (Newton steps, then bisection), for the tween that re-aims every frame. */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
    const cx = 3 * x1
    const bx = 3 * (x2 - x1) - cx
    const ax = 1 - cx - bx
    const cy = 3 * y1
    const by = 3 * (y2 - y1) - cy
    const ay = 1 - cy - by
    const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t
    const sampleY = (t: number) => ((ay * t + by) * t + cy) * t
    const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx
    return (x) => {
        if (x <= 0) return 0
        if (x >= 1) return 1
        let t = x
        for (let i = 0; i < 8; i++) {
            const err = sampleX(t) - x
            if (Math.abs(err) < 1e-5) return sampleY(t)
            const d = slopeX(t)
            if (Math.abs(d) < 1e-6) break
            t -= err / d
        }
        let lo = 0
        let hi = 1
        t = x
        for (let i = 0; i < 30; i++) {
            const v = sampleX(t)
            if (Math.abs(v - x) < 1e-5) break
            if (v < x) lo = t
            else hi = t
            t = (lo + hi) / 2
        }
        return sampleY(t)
    }
}
/** EASE.ios (cubic-bezier(0.32, 0.72, 0, 1)) as a function. */
const easeIos = cubicBezier(0.32, 0.72, 0, 1)

/** Call before the answer is sent (it notes which bubbles already exist), with the chip that was tapped. */
export function flyReplyToBubble(chip: HTMLElement): void {
    if (typeof window === 'undefined' || prefersReducedMotion() || typeof chip.animate !== 'function') return
    const root = chip.closest('[data-testid="agent-surface"]')
    if (!root) return
    const from = chip.getBoundingClientRect()
    if (!from.width || !from.height) return
    const before = new Set(root.querySelectorAll(USER_BUBBLE))

    const ghost = chip.cloneNode(true) as HTMLElement
    ghost.removeAttribute('data-testid')
    ghost.setAttribute('aria-hidden', 'true')
    ghost.classList.add('is-picked', 'is-ghost')
    Object.assign(ghost.style, {
        position: 'fixed',
        left: `${from.left}px`,
        top: `${from.top}px`,
        width: `${from.width}px`,
        height: `${from.height}px`,
        margin: '0',
        maxWidth: 'none',
        boxSizing: 'border-box',
        zIndex: '31',
        pointerEvents: 'none',
        transformOrigin: '0 0',
    })
    document.body.appendChild(ghost)

    let frames = 0
    let done = false
    const finish = (target?: HTMLElement) => {
        if (done) return
        done = true
        if (target) target.style.visibility = ''
        ghost.remove()
    }

    function land(target: HTMLElement, row: HTMLElement) {
        // Hidden with visibility: an entrance animation on the bubble may own its opacity. That entrance (the
        // phone chat's pop, on the message row) would play unseen under the copy and skew the measurement, so it
        // ends here.
        target.style.visibility = 'hidden'
        for (const a of row.getAnimations?.({ subtree: true }) ?? []) {
            try { a.finish() } catch { /* an infinite one: leave it */ }
        }
        // Re-aimed every frame: the bubble keeps moving while the question above it folds away (a WAAPI animation
        // whose keyframes change every frame never leaves its pending state, so this is a plain tween).
        ghost.style.transition = 'none'
        const duration = dur('push') + 40
        const start = performance.now()
        const step = (now: number) => {
            if (done) return
            if (!target.isConnected) return finish()
            const p = Math.min(1, Math.max(0, (now - start) / duration))
            const e = easeIos(p)
            const to = target.getBoundingClientRect()
            const sx = to.width / from.width
            const sy = to.height / from.height
            ghost.style.transform = `translate(${(to.left - from.left) * e}px, ${(to.top - from.top) * e}px) scale(${1 + (sx - 1) * e}, ${1 + (sy - 1) * e})`
            if (p < 1) requestAnimationFrame(step)
            else finish(target)
        }
        requestAnimationFrame(step)
        setTimeout(() => finish(target), duration + 400)
    }

    function dropAway() {
        const anim = ghost.animate(
            [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(28px) scale(0.96)' }],
            { duration: dur('base'), easing: EASE.in, fill: 'forwards' },
        )
        anim.onfinish = () => finish()
        setTimeout(() => finish(), dur('base') + 300)
    }

    function seek() {
        if (done) return
        const list = root!.querySelectorAll<HTMLElement>(USER_BUBBLE)
        const last = list[list.length - 1]
        if (last && !before.has(last)) {
            land(last.querySelector<HTMLElement>('.chat-bubble') ?? last, last)
            return
        }
        if (++frames > MAX_FRAMES) dropAway()
        else requestAnimationFrame(seek)
    }
    requestAnimationFrame(seek)
}
